import React, { useEffect, useState, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLearningStats } from '../contexts/learningStatsContext';
import { useQuizTranslator } from '../hooks/useQuizTranslator';
import { AngieSigns } from '../components/angieSigns';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { getLandmarks } from '../utils/apiCalls';
import { COMMON_PHRASES, CATEGORIES } from '../components/quizData';
import '../styles/signQuiz.css';

export function SignQuiz() {
    const navigate = useNavigate();
    const { category } = useParams();
    const { updateStats, completeQuiz } = useLearningStats();

    const [quizQuestions, setQuizQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState([]);
    const [currentAnswer, setCurrentAnswer] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [score, setScore] = useState(0);
    const [loading, setLoading] = useState(true);
    const [quizStarted, setQuizStarted] = useState(false);
    const [hasTrackedQuizStats, setHasTrackedQuizStats] = useState(false);

    const [landmarks, setLandmarks] = useState([]);
    const [replayKey, setReplayKey] = useState(0);
    
    const [cameraReady, setCameraReady] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [recordingTimeout, setRecordingTimeout] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [cameraInitializing, setCameraInitializing] = useState(false);
    
    const getDetectionScope = (category) => {
        switch(category) {
            case 'alphabets':
                return 'alpha';
            case 'numbers':
                return 'num';
            default:
                return 'glosses';
        }
    };

    const {
        videoRef,
        canvasRef1,
        canvasRef2,
        result,
        confidence,
        recording,
        startRecording,
        setResult,
    } = useQuizTranslator({
        detectionScope: getDetectionScope(category),
        forceModel: getDetectionScope(category)
    });

    const currentCategoryData = CATEGORIES[category] || CATEGORIES['alphabets'];
    const isPhrasesQuiz = category === 'phrases';

  

    const generateQuizQuestions = useCallback(() => {
        let animationQuestions, cameraQuestions;
        
        if (isPhrasesQuiz) {
            const shuffledPhrases = [...COMMON_PHRASES].sort(() => Math.random() - 0.5);
            
            animationQuestions = shuffledPhrases.slice(0, 3).map((phrase, index) => ({
                id: index + 1,
                type: 'animation',
                item: phrase.words[0],
                phrase: phrase,
                correctAnswer: phrase.phrase,
                displayAnswer: phrase.phrase
            }));
            
            cameraQuestions = shuffledPhrases.slice(3, 5).map((phrase, index) => ({
                id: index + 4,
                type: 'camera',
                item: phrase.words[0],
                phrase: phrase,
                correctAnswer: phrase.phrase,
                displayAnswer: phrase.phrase
            }));
        } else {
            const availableItems = currentCategoryData.items;
            const shuffled = [...availableItems].sort(() => Math.random() - 0.5);
            
            animationQuestions = shuffled.slice(0, 3).map((item, index) => ({
                id: index + 1,
                type: 'animation',
                item: item.toLowerCase(),
                correctAnswer: item,
                displayAnswer: item
            }));
            
            cameraQuestions = shuffled.slice(3, 5).map((item, index) => ({
                id: index + 4,
                type: 'camera',
                item: item.toLowerCase(),
                correctAnswer: item,
                displayAnswer: item
            }));
        }
        
        return [...animationQuestions, ...cameraQuestions].sort(() => Math.random() - 0.5);
    }, [currentCategoryData, isPhrasesQuiz]);

    const setupCamera = useCallback(async () => {
        try {
            setCameraError(null);
            setCameraInitializing(true);
            //console.log('Setting up camera...');
            
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { 
                    width: { ideal: 640 },
                    height: { ideal: 480 },
                    facingMode: 'user'
                }
            });
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                
                return new Promise((resolve) => {
                    videoRef.current.onloadedmetadata = () => {
                        //console.log('Video metadata loaded');
                        setCameraReady(true);
                        setCameraInitializing(false);
                        resolve(true);
                    };
                    
                    videoRef.current.oncanplay = () => {
                        //console.log('Video can play');
                        videoRef.current.play().catch(console.error);
                    };
                    
                    setTimeout(() => {
                        if (!cameraReady) {
                            //console.log('Camera initialization timeout');
                            setCameraInitializing(false);
                            resolve(false);
                        }
                    }, 5000);
                });
            }
            
        } catch (error) {
            console.error('Camera setup error:', error);
            setCameraError(error.message);
            setCameraReady(false);
            setCameraInitializing(false);
            
            let errorMessage = '';
            if (error.name === 'NotAllowedError') {
                errorMessage += 'Please allow camera access and try again.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += 'No camera found on your device.';
            } else {
                errorMessage += 'Please check your camera settings.';
            }
            
            alert(errorMessage);
            return false;
        }
    }, [videoRef, cameraReady]);

    const stopCamera = useCallback(() => {
        //console.log('Stopping camera...');
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach(track => {
                track.stop();
                console.log('Stopped track:', track.kind);
            });
            videoRef.current.srcObject = null;
        }
        setCameraReady(false);
        setCameraError(null);
        setCameraInitializing(false);
    }, [videoRef]);

    const loadAnimationLandmarks = useCallback(async (question) => {
        try {
            //console.log('Loading animation for item:', question.item);
            const data = await getLandmarks(question.item);
            
            if (!data || data.length === 0) {
                console.warn('No landmarks found for:', question.item);
                setLandmarks([]);
            } else {
                setLandmarks(data);
            }
            setReplayKey(prev => prev + 1);
        } catch (error) {
            console.error('Failed to load landmarks:', error);
            setLandmarks([]);
        }
    }, []);

    const handleStartRecording = useCallback(async () => {
        if (!cameraReady) {
            const success = await setupCamera();
            if (!success) return;
        }
        
        if (recordingTimeout) {
            clearTimeout(recordingTimeout);
        }

        const modelType = getDetectionScope(category);
        //console.log(`Starting recording for ${category} category using ${modelType} model`);
        startRecording();
        setCountdown(5);
        
        const countdownInterval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownInterval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        const timeout = setTimeout(() => {
            //console.log('Auto-stopping recording after 5 seconds');
            if (recording) {
                startRecording(); 
            }
            clearInterval(countdownInterval);
            setRecordingTimeout(null);
            setCountdown(0);
        }, 5000);

        setRecordingTimeout(timeout);
    }, [recording, startRecording, recordingTimeout, category, cameraReady, setupCamera]);

    const handleTryAgain = useCallback(() => {
        setResult("");
        if (recordingTimeout) {
            clearTimeout(recordingTimeout);
            setRecordingTimeout(null);
        }
        setCountdown(0);
        
        if (recording) {
            startRecording();
        }
    }, [recording, startRecording, recordingTimeout, setResult]);

    const handleReplay = () => {
        setReplayKey(prev => prev + 1);
    };

    const skipQuestion = () => {
        const currentQuestion = quizQuestions[currentQuestionIndex];
        
        const skippedAnswer = {
            questionId: currentQuestion.id,
            userAnswer: 'SKIPPED',
            correctAnswer: currentQuestion.correctAnswer,
            isCorrect: false,
            questionItem: isPhrasesQuiz ? currentQuestion.phrase.phrase : currentQuestion.item,
            type: currentQuestion.type,
            skipped: true
        };

        const newUserAnswers = [...userAnswers, skippedAnswer];
        setUserAnswers(newUserAnswers);

        setCurrentAnswer('');
        setResult("");
        stopCamera();

        if (currentQuestionIndex + 1 < quizQuestions.length) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setShowResults(true);
            const finalScore = newUserAnswers.filter(answer => answer.isCorrect).length;
            const percentage = Math.round((finalScore / quizQuestions.length) * 100);

            if (!hasTrackedQuizStats) {
                //console.log(`Quiz completed for ${category}. Score: ${finalScore}/${quizQuestions.length} (${percentage}%)`);
                
                if (percentage >= 60) { 
                    completeQuiz(category);
                    
                    const event = new CustomEvent('quizCompleted', {
                        detail: { category, score: finalScore, passed: true }
                    });
                    window.dispatchEvent(event);
                } else {
                    const event = new CustomEvent('quizCompleted', {
                        detail: { 
                            category, 
                            score: finalScore, 
                            passed: false,
                            percentage 
                        }
                    });
                    window.dispatchEvent(event);
                }
                
                updateStats(prevStats => ({
                    ...prevStats,
                    quizzesCompleted: (prevStats?.quizzesCompleted || 0) + 1,
                    totalQuizQuestions: (prevStats?.totalQuizQuestions || 0) + quizQuestions.length,
                    lastQuizScore: finalScore,
                    lastQuizCategory: category
                }));
                
                setHasTrackedQuizStats(true);
            }
        }
    };

    const startQuiz = () => {
        setQuizStarted(true);
        setCurrentQuestionIndex(0);
        setUserAnswers([]);
        setCurrentAnswer('');
        setShowResults(false);
        setScore(0);
        setHasTrackedQuizStats(false);
    };

    const handleAnswerSubmit = (answer = null) => {
        const currentQuestion = quizQuestions[currentQuestionIndex];
        const answerToCheck = answer || currentAnswer;
        
        if (!answerToCheck.trim()) return;

        let isCorrect;
        if (category === 'alphabets') {
            isCorrect = answerToCheck.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase();
        } else if (isPhrasesQuiz) {
            const userWords = answerToCheck.toLowerCase().trim().split(/\s+/);
            const correctWords = currentQuestion.correctAnswer.toLowerCase().trim().split(/\s+/);
            const matchCount = userWords.filter(word => correctWords.includes(word)).length;
            isCorrect = matchCount >= Math.ceil(correctWords.length * 0.7);
        } else {
            isCorrect = answerToCheck.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase();
        }

        const newAnswer = {
            questionId: currentQuestion.id,
            userAnswer: answerToCheck.trim(),
            correctAnswer: currentQuestion.correctAnswer,
            isCorrect,
            questionItem: isPhrasesQuiz ? currentQuestion.phrase.phrase : currentQuestion.item,
            type: currentQuestion.type
        };

        const newUserAnswers = [...userAnswers, newAnswer];
        setUserAnswers(newUserAnswers);

        if (isCorrect) {
            setScore(score + 1);
        }

        setCurrentAnswer('');
        stopCamera();

        if (currentQuestionIndex + 1 < quizQuestions.length) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setShowResults(true);
            const finalScore = newUserAnswers.filter(answer => answer.isCorrect).length;
            const percentage = Math.round((finalScore / quizQuestions.length) * 100);

            if (!hasTrackedQuizStats) {
                //console.log(`Quiz completed for ${category}. Score: ${finalScore}/${quizQuestions.length} (${percentage}%)`);
                
                if (percentage >= 60) { 
                    completeQuiz(category);
                    
                    const event = new CustomEvent('quizCompleted', {
                        detail: { category, score: finalScore, passed: true }
                    });
                    window.dispatchEvent(event);
                }
                
                updateStats(prevStats => ({
                    ...prevStats,
                    quizzesCompleted: (prevStats?.quizzesCompleted || 0) + 1,
                    totalQuizQuestions: (prevStats?.totalQuizQuestions || 0) + quizQuestions.length,
                    lastQuizScore: finalScore,
                    lastQuizCategory: category
                }));
                
                setHasTrackedQuizStats(true);
            }
        }
    };

    const handleCameraAnswerSubmit = () => {
        let cleanResult = result
            .replace('Detected: ', '')
            .replace('Detected phrase: ', '')
            .replace('API Result: ', '')
            .trim();
            
        const modelType = getDetectionScope(category);
        //console.log(`Camera result for ${category} (${modelType} model):`, cleanResult);
        
        if (cleanResult && cleanResult !== "" && cleanResult !== "") {
            handleAnswerSubmit(cleanResult);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAnswerSubmit();
        }
    };

    const restartQuiz = () => {
        setHasTrackedQuizStats(false);
        stopCamera();
        const questions = generateQuizQuestions();
        setQuizQuestions(questions);
        startQuiz();
    };

    const goBackToCategory = () => {
        stopCamera();
        navigate('/learn', { state: { selectedCategory: category } });
    };

    const goBackToLearn = () => {
        stopCamera();
        navigate('/learn');
    };

    useEffect(() => {
        const questions = generateQuizQuestions();
        setQuizQuestions(questions);
        setLoading(false);
    }, [generateQuizQuestions]);

    useEffect(() => {
        if (quizStarted && quizQuestions.length > 0 && currentQuestionIndex < quizQuestions.length) {
            const currentQuestion = quizQuestions[currentQuestionIndex];
            
            if (currentQuestion.type === 'animation') {
                stopCamera();
                loadAnimationLandmarks(currentQuestion);
            } else if (currentQuestion.type === 'camera') {
                //console.log(`Camera question loaded - using ${getDetectionScope(category)} model`);
                if (!cameraReady && !cameraError) {
                    setupCamera();
                }
                if (canvasRef2.current && videoRef.current) {
                    setTimeout(() => {
                        const canvas = canvasRef2.current;
                        const video = videoRef.current;
                        if (video && video.readyState >= 2) {
                            canvas.width = video.videoWidth * 0.5;
                            canvas.height = video.videoHeight * 0.5;
                        }
                    }, 1000);
                }
            }
        }
    }, [quizStarted, currentQuestionIndex, quizQuestions, loadAnimationLandmarks, stopCamera, setResult, cameraReady, cameraError, setupCamera, category, currentCategoryData.name, videoRef, canvasRef2]);

    useEffect(() => {
        return () => {
            //console.log('Cleaning up SignQuiz component...');
            stopCamera();
            if (recordingTimeout) {
                clearTimeout(recordingTimeout);
            }
        };
    }, [stopCamera, recordingTimeout]);

    if (loading) {
        return (
            <div className="quizcontainer">
                <h1 className="loading-text">Loading {currentCategoryData.name} Quiz...</h1>
            </div>
        );
    }

    if (!quizStarted) {
        return (
            <div className="quizcontainer">
                <button onClick={goBackToCategory} className="quizback-button">
                    ← Back to {currentCategoryData.name}
                </button>

                <div className="quiz-card">
                    <h2 className="quiz-title">
                        {currentCategoryData.name} QUIZ
                    </h2>
                    <p className="quiz-description">
                        This quiz has 5 questions:
                        <br />
                        <strong>3 questions:</strong> Watch animations and type answers
                        <br />
                        <strong>2 questions:</strong> Sign with your camera
                        <br />
                        <small className="quiz-subtitle">
                            {isPhrasesQuiz ? 
                                "Watch phrase animations or sign phrases yourself!" :
                                `Test your knowledge of ${currentCategoryData.name.toLowerCase()} signs!`
                            }
                        </small>
                        <br />
                    </p>

                    <button onClick={startQuiz} className="start-button">
                        Start Quiz!
                    </button>
                </div>
            </div>
        );
    }

    if (showResults) {
        const percentage = Math.round((score / quizQuestions.length) * 100);
        const passed = percentage >= 60;

        return (
            <div className="quizcontainer">
                <div className="result-card">
                    <h1 className={`result-title ${passed ? 'passed' : 'failed'}`}>
                        {passed ? 'Great Job!' : 'Keep Learning!'}
                    </h1>

                    <h2 className="category-title">
                        {currentCategoryData.name} Quiz Results
                    </h2>

                    <div className="score-summary">
                        <p><strong>Total Score: {score}/{quizQuestions.length} ({percentage}%)</strong></p>
                        <p className="score-breakdown">
                            Animation Questions: {userAnswers.filter(a => a.type === 'animation' && a.isCorrect).length}/3
                            <br />
                            Camera Questions: {userAnswers.filter(a => a.type === 'camera' && a.isCorrect).length}/2
                        </p>
                        {passed && (
                            <p className="pass-message">
                            Quiz Passed! {percentage >= 60 ? 'Next category unlocked!' : ''}
                            </p>
                        )}
                    </div>

                    <div className="answers-section">
                        <h3 className="section-title">Questions You Got Correct:</h3>
                        {userAnswers.filter(answer => answer.isCorrect).length > 0 ? (
                            <div className="answers-list">
                                {userAnswers.filter(answer => answer.isCorrect).map((answer, index) => (
                                    <div key={index} className="answer-item correct">
                                        <div>
                                            <strong>Question {userAnswers.indexOf(answer) + 1}: {answer.correctAnswer}</strong>
                                            <br />
                                            <span className="answer-details">
                                                Your answer: {answer.userAnswer} ({answer.type === 'camera' ? 'Camera' : 'Animation'})
                                            </span>
                                        </div>
                                        <div className="check-mark">✓</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="no-answers">No correct answers this time.</p>
                        )}

                        {userAnswers.filter(answer => !answer.isCorrect).length > 0 && (
                            <>
                                <h3 className="section-title">Questions You Got Wrong:</h3>
                                {userAnswers.filter(answer => !answer.isCorrect).map((answer, index) => (
                                    <div key={index} className="answer-item incorrect">
                                        <div>
                                            <strong>Question {userAnswers.indexOf(answer) + 1}: {answer.correctAnswer}</strong>
                                            <br />
                                            <span className="answer-details wrong">
                                                Your answer: {answer.skipped ? 'SKIPPED' : answer.userAnswer} ({answer.type === 'camera' ? 'Camera' : 'Animation'})
                                            </span>
                                            <br />
                                            <span className="answer-details correct-answer">
                                                Correct answer: {answer.correctAnswer}
                                            </span>
                                        </div>
                                        <div className="x-mark">✗</div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    <div className="result-buttons">
                        <button onClick={restartQuiz} className="button primary">
                            Try Again
                        </button>
                        <button onClick={goBackToCategory} className="button secondary">
                            Back to {currentCategoryData.name}
                        </button>
                        <button onClick={goBackToLearn} className="button tertiary">
                            Back to Learn
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentQuestion = quizQuestions[currentQuestionIndex];
    const isAnimationQuestion = currentQuestion?.type === 'animation';
    const isCameraQuestion = currentQuestion?.type === 'camera';

    const cleanResult = result
        .replace('Detected: ', '')
        .replace('Detected phrase: ', '')
        .replace('API Result: ', '')
        .trim();
    
    const hasValidCameraResult = cleanResult && cleanResult !== "Click 'Start Signing' to begin" && cleanResult !== "";

    return (
        <div className="quiz-container">
            <button onClick={goBackToCategory} className="quizback-button">
                ← Back to {currentCategoryData.name}
            </button>


            {isAnimationQuestion && (
                <div className="animation-section">

                <h1 className="quiz-title-active">
                    {currentCategoryData.name} Quiz
                </h1>
                <p className="question-info">
                    Question {currentQuestionIndex + 1} of {quizQuestions.length} 
                    ({isAnimationQuestion ? 'Watch & Type' : 'Sign with Camera'})
                </p>
                <div className="progress-bar">
                    <div 
                        className="progress-fill"
                        style={{ width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%` }}
                    ></div>
                </div>


                    <h2 className="question-title">
                        {isPhrasesQuiz ? 'What phrase is being signed?' : 'What is this sign?'}
                    </h2>

                    <div className="canvas-container">
                        <Canvas camera={{ position: [0, 0.2, 3], fov: 30 }}>
                            {/* eslint-disable react/no-unknown-property */}
                            <ambientLight intensity={10} />
                            {/* eslint-disable react/no-unknown-property */}
                            <group position={[0, -1.1, 0]}>             
                                {landmarks && Object.keys(landmarks).length > 0 && (
                                    <AngieSigns key={replayKey} landmarks={landmarks} />
                                )}
                            </group>  
                            <OrbitControls enablePan={false} maxPolarAngle={Math.PI / 2} minDistance={2} maxDistance={3} />
                        </Canvas>
                    </div>

                    <button onClick={handleReplay} className="replay-button">
                        Replay Animation
                    </button>

                    <div className="answer-input-section">
                        <input
                            type="text"
                            value={currentAnswer}
                            onChange={(e) => setCurrentAnswer(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your answer..."
                            className="answer-input"
                            style={{
                                textTransform: category === 'alphabets' ? 'uppercase' : 'none'
                            }}
                            autoFocus
                        />

                        <div className="button-row">
                            <button
                                onClick={() => handleAnswerSubmit()}
                                disabled={!currentAnswer.trim()}
                                className={`button primary ${!currentAnswer.trim() ? 'disabled' : ''}`}
                            >
                                Submit Answer
                            </button>
                            <button onClick={skipQuestion} className="button skip">
                                Skip Question →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isCameraQuestion && (
                <div className="camera-section">
                     <h1 className="quiz-title-active">
                    {currentCategoryData.name} Quiz
                </h1>
                <p className="question-info">
                    Question {currentQuestionIndex + 1} of {quizQuestions.length} 
                    ({isAnimationQuestion ? 'Watch & Type' : 'Sign with Camera'})
                </p>
                <div className="progress-bar">
                    <div 
                        className="progress-fill"
                        style={{ width: `${((currentQuestionIndex + 1) / quizQuestions.length) * 100}%` }}
                    ></div>
                </div>

                    <h2 className="question-title">
                        Sign: &quot;{currentQuestion.correctAnswer}&quot;
                    </h2>

                    <div className="camera-container">
                        <div className="rrecognizer-camera-container relative">
                            <video 
                                ref={videoRef} 
                                autoPlay 
                                playsInline 
                                className="rrecognizer-video"
                                style={{ display: cameraReady ? 'block' : 'none' }}
                            ></video>
                            
                            {cameraInitializing && (
                                <div className="camera-status-overlay">
                                    <div className="camera-status-text">
                                        Initializing camera for {getDetectionScope(category)} detection...
                                    </div>
                                </div>
                            )}
                            
                            {cameraError && (
                                <div className="camera-status-overlay error">
                                    <div className="camera-status-text">
                                        Camera Error: {cameraError}
                                        <br />
                                        <button 
                                            onClick={setupCamera}
                                            className="button secondary"
                                            style={{ marginTop: '10px' }}
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            <canvas 
                                ref={canvasRef2} 
                                style={{ position: 'absolute', top: 0, left: '5%', zIndex: 1, display: cameraReady ? 'block' : 'none' }}
                            ></canvas>
                            <canvas 
                                ref={canvasRef1} 
                                style={{ position: 'absolute', bottom: 0, left: '30%', zIndex: 1, display: 'none' }}
                            ></canvas>
                            
                            {recording && cameraReady && (
                                <div className="rrecognizer-recording-indicator">
                                    <i className="fas fa-circle recognizer-pulse-icon"></i> 
                                    Recording... {countdown > 0 && `(${countdown}s)`}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="camera-controls">
                        <div className="rrecognizer-controls">
                            <button onClick={() => setResult("")} className="rrecognizer-control-button rrecognizer-capture-button">
                                Clear Results
                            </button>
                            <button 
                                onClick={handleStartRecording} 
                                className={`rrecognizer-control-button ${recording ? 'rrecognizer-stop-button' : 'rrecognizer-record-button'}`}
                            >
                                <i className={`fas ${recording ? 'fa-stop' : 'fa-video'}`}></i> 
                                {recording ? 'Stop Signing' : 'Start Signing'}
                            </button>
                        </div>

                        {hasValidCameraResult && (
                            <div className="result-section">
                                <div className="detection-result">
                                    Detected: &quot;{cleanResult}&quot;
                                </div>
                                
                                <div className="confidence-score">
                                    Confidence: {confidence}
                                </div>
                                
                                <div className="button-row">
                                    <button
                                        onClick={handleCameraAnswerSubmit}
                                        className="button primary"
                                    >
                                        ✓ Submit This Answer
                                    </button>
                                    <button
                                        onClick={handleTryAgain}
                                        className="button secondary"
                                    >
                                        Try Again
                                    </button>
                                    <button onClick={skipQuestion} className="button skip">
                                        Skip Question →
                                    </button>
                                </div>
                            </div>
                        )}

                        {cameraReady && !hasValidCameraResult && !recording && (
                            <button onClick={skipQuestion} className="button skip">
                                Skip Question →
                            </button>
                        )}
                    </div>

                    <div className="instructions">
                        <p>
                            <strong>Instructions:</strong> Position your hands clearly in the camera frame and sign &quot;{currentQuestion.correctAnswer}&quot;.
                            
                        </p>
                    </div>
                </div>
            )}

            
        </div>
    );
}